
import { useStore } from '@/store'
import { BetType, EventBuss } from '@/types/global'
import { abbreviateNumber } from '@/utils'
import {
    ChipOption,
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    StyleValue,
    TableData
} from 'vue'
import { useI18n } from 'vue-i18n'

const CHIP_SIZE = 14
const CHIP_SIZE_SPECIAL = 14

export default defineComponent({
    emits: ['place-bet', 'on-error'],
    props: {
        label: {
            required: false,
            default: '',
            type: String as PropType<string>
        },
        ratio: {
            required: false,
            default: '',
            type: String as PropType<string>
        },
        color: {
            type: Number as PropType<number>,
            required: true
        },
        confirmedBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        tempBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        totalBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        betType: {
            type: Number as PropType<number>,
            required: true
        },
        width: {
            type: Number as PropType<number>,
            required: true
        },
        height: {
            type: Number as PropType<number>,
            required: true
        },
        desk: {
            type: Object as PropType<TableData>,
            required: false
        },
        chipsAttr: {
            type: Object as PropType<{
                width: number
                height: number
                offset: number
            }>,
            default: () => ({ width: 16, height: 16, offset: 5 }),
            required: false
        },
        isGrayMode: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        allowBet: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        errorMessage: {
            type: String as PropType<string>,
            required: false,
            default: ''
        }
    },
    setup(props, { emit }) {
        const emitter = inject('emitter') as Emitter
        const store = useStore()
        const { t } = useI18n()
        const chipsContainerRef = ref<HTMLElement>()
        const chipsUI = ref<
            {
                x: number
                y: number
                amount: number
                class: string
                type: 'temp' | 'confirmed'
            }[]
        >([])

        onMounted(() => {
            emitter.on(EventBuss.PLACE_CHIP, handleOnPlaceChip)
        })

        onBeforeUnmount(() => {
            emitter.off(EventBuss.PLACE_CHIP, handleOnPlaceChip)
        })

        const playMode = computed((): number => store.getters['playMode'])
        const isSpecialMode = computed(
            () =>
                playMode.value === 2 &&
                props.desk?.gameTypeNo?.toUpperCase() === 'G002'
        )

        const getRandomPosition = (min: number, max: number) => {
            if (max <= min) return min
            return Math.floor(Math.random() * (max - min + 1) + min)
        }

        const getChipPositionStyle = (post: number): string => {
            return `calc(var(--pc1)*${post})`
        }

        const chipAreaSize = computed(() => {
            let w = Math.abs(props.width)
            let h = Math.abs(props.height)

            if (isSpecialMode.value) {
                // 3-row special layout is denser — shrink chip area to stay in bounds
                w = Math.max(Math.floor(w * 0.85), CHIP_SIZE_SPECIAL * 2)
                h = Math.max(Math.floor(h * 0.6), CHIP_SIZE_SPECIAL * 2)
            }

            return { w, h }
        })

        const chipSize = computed(() =>
            isSpecialMode.value ? CHIP_SIZE_SPECIAL : CHIP_SIZE
        )

        const chipContainerStyle = computed((): StyleValue | undefined => {
            if (props.width > 0 && props.height > 0) {
                const { w, h } = chipAreaSize.value
                return {
                    width: `calc(var(--pc1)*${w})`,
                    height: `calc(var(--pc1)*${h})`
                }
            }
            return undefined
        })

        const betColor = computed(() => {
            const { color } = props

            let result = ''
            if (color === 1) result = 'red'
            else if (color === 2) result = 'green'
            else if (color === 3) result = 'blue'
            else if (color === 4) result = 'super6'

            if (props.isGrayMode) return 'gray'
            return result
        })

        const sumOfTempBetandConfirmedBet = computed(() => {
            const confirmedBet = props.confirmedBet ?? 0
            const tempBet = props.tempBet ?? 0
            return confirmedBet + tempBet
        })

        const toShortValue = (value: number) => {
            return abbreviateNumber(value)
        }

        const placeBet = () => {
            if (props.isGrayMode && props.allowBet) {
                let error = props.errorMessage
                if (!error) {
                    if (props.betType === BetType.DRAGON_7) {
                        error = t('prompts.d7error')
                    } else if (props.betType === BetType.BEAR_8) {
                        error = t('prompts.b8error')
                    } else if (props.betType === BetType.BIG_TIGER) {
                        error = t('prompts.bigtigererror')
                    } else if (props.betType === BetType.SMALL_TIGER) {
                        error = t('prompts.smalltigererror')
                    } else if (props.betType === BetType.SUPER6) {
                        error = t('prompts.super6tigermutual')
                    }
                }

                if (error) emit('on-error', error)
                return
            }

            if (!props.allowBet && props.isGrayMode) {
                return
            }

            emit('place-bet', props.betType)
        }

        const removeTempUIBet = () => {
            chipsUI.value = chipsUI.value.filter((i) => i.type !== 'temp')
        }

        const applyTempUIBet = () => {
            chipsUI.value = chipsUI.value.map((i) => {
                if (i.type === 'temp') i.type = 'confirmed'
                return i
            })

            // console.log(chipsUI.value)
        }

        const breakAmountIntoChips = (amount: number): number[] => {
            const values = [...chips.value.value]
                .filter((v) => typeof v === 'number' && v > 0)
                .sort((a, b) => b - a)
            const parts: number[] = []
            let remaining = amount
            for (const v of values) {
                while (remaining >= v) {
                    parts.push(v)
                    remaining -= v
                }
            }
            if (remaining > 0) parts.push(remaining)
            return parts.length > 0 ? parts : [amount]
        }

        const pushChipUI = (amount: number, type: 'temp' | 'confirmed') => {
            const { w, h } = chipAreaSize.value
            const size = chipSize.value
            const chipHalf = size / 2

            let startY = Math.floor(h / 2)
            let startX = Math.floor(w / 2)

            if (chipsUI.value.length > 0) {
                const lastItem = chipsUI.value[chipsUI.value.length - 1]
                startY = lastItem.y
                startX = lastItem.x
            }

            let _x = getRandomPosition(chipHalf, Math.max(chipHalf, w - chipHalf))
            let xTries = 0
            while (Math.abs(_x - startX) > size && xTries < 20) {
                _x = getRandomPosition(chipHalf, Math.max(chipHalf, w - chipHalf))
                if (_x + size > w) {
                    _x = w - chipHalf
                }
                xTries++
            }

            let _y = getRandomPosition(chipHalf, Math.max(chipHalf, h - chipHalf))
            let yTries = 0
            while (Math.abs(_y - startY) > size && yTries < 20) {
                _y = getRandomPosition(chipHalf, Math.max(chipHalf, h - chipHalf))
                if (_y + size > h) {
                    _y = h - chipHalf
                }
                yTries++
            }

            let _chipIndex = chips.value.value.indexOf(amount)
            if (_chipIndex === -1) _chipIndex = 22

            chipsUI.value.push({
                x: _x,
                y: _y,
                amount,
                class: `_chip_${_chipIndex}`,
                type
            })
        }

        const handleOnPlaceChip = (arg: {
            t: number
            amt: number
            type: 'temp' | 'confirmed'
            deskId: number
        }) => {
            if (props.desk && props.desk.id !== arg.deskId) return

            if (arg.t !== props.betType) return

            if (arg.type === 'confirmed') {
                const existingSum = chipsUI.value.reduce(
                    (sum, chip) => sum + chip.amount,
                    0
                )
                // Keep the chips the player placed (e.g. 3x100) instead of
                // collapsing them into one total chip after confirm/sync.
                if (chipsUI.value.length > 0 && existingSum === arg.amt) {
                    chipsUI.value = chipsUI.value.map((chip) => ({
                        ...chip,
                        type: 'confirmed'
                    }))
                    return
                }

                chipsUI.value = []
                breakAmountIntoChips(arg.amt).forEach((amount) => {
                    pushChipUI(amount, 'confirmed')
                })
                return
            }

            pushChipUI(arg.amt, arg.type)
        }

        const resetBetUI = () => {
            chipsUI.value = []
        }

        const chips = computed((): ChipOption => store.getters['chips'])

        return {
            betColor,
            isSpecialMode,
            sumOfTempBetandConfirmedBet,
            chipsUI,
            chipsContainerRef,
            chipContainerStyle,
            placeBet,
            toShortValue,
            handleOnPlaceChip,
            resetBetUI,
            removeTempUIBet,
            applyTempUIBet,
            getChipPositionStyle
        }
    }
})
